import { Message, MessagePartTool } from '@varos/assistant-sdk';
import { assertNever } from '@varos/util-typescript';
import { IconType } from 'react-icons';
import { AiOutlineSearch } from 'react-icons/ai';
import { BsFileEarmarkDiff } from 'react-icons/bs';
import { GoTasklist, GoWorkflow } from 'react-icons/go';
import React, { useEffect } from 'react';
import { ReportingShellControllerBaseConfig } from '../../../base';
import { ChatDetailController } from './chatDetailInterface';
import { ChatDetailViewProps } from './chatDetailProps';
import { ChatDetailAggregate } from './chatDetailModel';
import { ChatMessageProps, MessagePartItemProps, MessagePartProps } from './message';

export function createChatDetailController(
    config: ReportingShellControllerBaseConfig
): ChatDetailController {
    function getToolProps(tool: MessagePartTool): {
        Icon: IconType | null;
        label: string;
    } {
        if (tool.toolInvocation.toolName === 'read_document') {
            return {
                Icon: AiOutlineSearch,
                label: `Reading document`,
            };
        }
        if (tool.toolInvocation.toolName === 'search_documents') {
            return {
                Icon: AiOutlineSearch,
                label: `Searching documents`,
            };
        }
        if (tool.toolInvocation.toolName === 'create_suggestion') {
            return {
                Icon: BsFileEarmarkDiff,
                label: `Generating suggestions`,
            };
        }
        if (tool.toolInvocation.toolName === 'apply_suggestion') {
            return {
                Icon: AiOutlineSearch,
                label: `Applying`,
            };
        }
        if (tool.toolInvocation.toolName === 'create_document') {
            return {
                Icon: BsFileEarmarkDiff,
                label: `Creating document`,
            };
        }
        if (tool.toolInvocation.toolName === 'get_task_status') {
            return {
                Icon: GoWorkflow,
                label: `Checking status`,
            };
        }
        if (tool.toolInvocation.toolName === 'get_task_description') {
            return {
                Icon: GoTasklist,
                label: `Getting tasks`,
            };
        }
        if (tool.toolInvocation.toolName === 'determine_workflow') {
            return {
                Icon: GoWorkflow,
                label: `Determining workflow`,
            };
        }
        if (tool.toolInvocation.toolName === 'update_thread') {
            return {
                Icon: GoWorkflow,
                label: `Updating context`,
            };
        }
        if (tool.toolInvocation.toolName === 'start_workflow') {
            return {
                Icon: GoWorkflow,
                label: `Starting workflow`,
            };
        }
        if (tool.toolInvocation.toolName === 'replace_document') {
            return {
                Icon: BsFileEarmarkDiff,
                label: `Replacing document`,
            };
        }
        assertNever(tool.toolInvocation);
    }

    function buildMessageProps(
        item: ChatDetailAggregate,
        message: Message,
        isStreaming: boolean = false
    ): ChatMessageProps {
        const [selection] = message.context.filter(
            (context): context is Extract<typeof context, { kind: 'selection' }> =>
                context.kind === 'selection'
        );

        return {
            id: message.id,
            role: message.role,
            parts: (() => {
                const processedParts: MessagePartProps[] = [];

                for (let i = 0; i < message.parts.length; i++) {
                    const part = message.parts[i];

                    if (part.type === 'text') {
                        processedParts.push({
                            type: 'text',
                            text: part.text,
                        });
                    } else if (part.type === 'tool-invocation') {
                        // Determine status based on the tool's state and streaming status
                        let status: 'running' | 'done' | 'failure';

                        if (isStreaming) {
                            // For streaming messages, check the tool state
                            status =
                                part.toolInvocation.state === 'result'
                                    ? 'done'
                                    : 'running';
                        } else {
                            // For completed messages, use the tool state directly
                            status =
                                part.toolInvocation.state === 'result'
                                    ? 'done'
                                    : 'running';
                        }

                        const toolProps = getToolProps(part);

                        let item: MessagePartItemProps | null = null;

                        if (part.toolInvocation.toolName === 'apply_suggestion') {
                            item = {
                                kind: 'apply',
                                data: {
                                    item: {
                                        tool: part.toolInvocation,
                                        result: part.toolInvocation.result ?? null,
                                    },
                                },
                            };
                        }

                        if (
                            part.toolInvocation.toolName === 'create_document' &&
                            part.toolInvocation.result &&
                            Object.keys(part.toolInvocation.result).length !== 0
                        ) {
                            item = {
                                kind: 'create',
                                data: {
                                    item: {
                                        tool: part.toolInvocation,
                                        result: part.toolInvocation.result,
                                    },
                                },
                            };
                        }

                        if (part.toolInvocation.toolName === 'search_documents') {
                            item = {
                                kind: 'search',
                                data: {
                                    item: {
                                        tool: part.toolInvocation,
                                        result: part.toolInvocation.result ?? null,
                                    },
                                },
                            };
                        }

                        if (part.toolInvocation.toolName === 'read_document') {
                            item = {
                                kind: 'read',
                                data: {
                                    item: {
                                        tool: part.toolInvocation,
                                        result: part.toolInvocation.result ?? null,
                                    },
                                },
                            };
                        }

                        if (part.toolInvocation.toolName === 'start_workflow') {
                            item = {
                                kind: 'task',
                                data: {
                                    item: {
                                        tool: part.toolInvocation,
                                        result: part.toolInvocation.result ?? null,
                                    },
                                },
                            };
                        }

                        if (part.toolInvocation.toolName === 'replace_document') {
                            item = {
                                kind: 'replace',
                                data: {
                                    item: {
                                        tool: part.toolInvocation,
                                        result: part.toolInvocation.result ?? null,
                                    },
                                },
                            };
                        }

                        processedParts.push({
                            type: 'tool-invocation',
                            status,
                            label: toolProps.label,
                            Icon: toolProps.Icon ?? null,
                            item,
                        });
                    }
                    // Skip step-start and step-end parts - they're used as delimiters only
                }

                return processedParts;
            })(),
            context: selection
                ? {
                      // TODO make the title based on the actual context by resolving the content on the selection
                      //   title: item.content?.title ?? 'no title',
                      title: 'no title',
                      text: selection.data.plaintext,
                      range: {
                          label: `L${selection.data.start.line + 1}:${selection.data.end.line + 1}`,
                      },
                  }
                : null,
        };
    }

    return {
        useProps(context, item, props): ChatDetailViewProps {
            // Scroll state management
            const [isScrolling, setIsScrolling] = React.useState(false);
            const scrollTimeoutRef = React.useRef<NodeJS.Timeout>();
            const messagesContainerRef = React.useRef<HTMLDivElement>(null);
            const [isUserScrolledUp, setIsUserScrolledUp] = React.useState(false);

            const handleScroll = React.useCallback(() => {
                setIsScrolling(true);

                // Check if user is scrolled up from bottom
                const container = messagesContainerRef.current;
                if (container) {
                    const { scrollTop, scrollHeight, clientHeight } = container;
                    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
                    setIsUserScrolledUp(!isAtBottom);
                }

                // Clear existing timeout
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }

                // Set new timeout to hide scrollbar
                scrollTimeoutRef.current = setTimeout(() => {
                    setIsScrolling(false);
                }, 1000); // Hide after 1 second of no scrolling
            }, []);

            // Auto-scroll to bottom when new messages arrive (unless user scrolled up)
            React.useEffect(() => {
                if (!isUserScrolledUp && messagesContainerRef.current) {
                    const container = messagesContainerRef.current;
                    container.scrollTop = container.scrollHeight;
                }
            }, [item.messages, isUserScrolledUp]);

            React.useEffect(() => {
                return () => {
                    if (scrollTimeoutRef.current) {
                        clearTimeout(scrollTimeoutRef.current);
                    }
                };
            }, []);

            const messageItems = React.useMemo(
                () =>
                    item.messages?.map((message) =>
                        buildMessageProps(item, message, false)
                    ) ?? [],
                [item, item.messages]
            );

            const isLoading = props.chat.isLoading;

            const isAutoGenerating = React.useRef(false);

            // Auto-generate reply if no assistant messages are present
            useEffect(() => {
                const assistantMessages = props.chat.messages.filter(
                    (message) => message.role === 'assistant'
                );
                if (
                    !isLoading &&
                    assistantMessages.length === 0 &&
                    !isAutoGenerating.current
                ) {
                    isAutoGenerating.current = true;
                    props.onGenerate();
                }
            }, [isLoading, props.chat.messages, props.onGenerate]);

            return {
                // overlay: {
                //     close: {
                //         button: {
                //             onClick() {
                //                 props.overlay.disclosure.onClose();
                //             },
                //         },
                //     },
                //     new: {
                //         button: {
                //             onClick() {
                //                 return props.thread.onCreate();
                //             },
                //         },
                //     },
                // },
                message: {
                    items: messageItems,
                    // streaming: streamingMessage,
                    streaming: null,
                    container: {
                        ref: messagesContainerRef,
                        onScroll: handleScroll,
                        isScrolling,
                    },
                },
                composer: {
                    isLoading: isLoading,
                    container: {
                        onSubmit: props.onSubmit,
                        thread: item.thread?.id ?? null,
                    },
                },
            };
        },
    };
}
